import React from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../service/Api';
import { useAuth } from '../../context/AuthContext';
import { Loader2, Brain, Sparkles, CheckCircle } from 'lucide-react';
import { useUserIntelligence } from '../../hooks/useUserIntelligence';
import UserIntelligenceForm from '../../components/intelligence/UserIntelligenceForm';

export default function OnboardingStep3() {
  const navigate = useNavigate();
  const { checkAuthStatus } = useAuth();
  const [skipping, setSkipping] = React.useState(false);

  const {
    inputData,
    setInputData,
    generating,
    error,
    successMessage,
    createIntelligence,
    setError
  } = useUserIntelligence();

  const handleGenerate = async () => {
    const result = await createIntelligence(inputData);
    if (result.success) {
      // Redirect to dashboard for product tour instead of completing onboarding
      navigate("/tailspin/dashboard");
    }
  };

  const handleSkip = async () => {
    try {
      setSkipping(true);
      console.log("navigating to dashboard");
      // Redirect to dashboard for product tour instead of completing onboarding
      navigate("/tailspin/dashboard");
    } catch (err) {
      console.error('Error skipping step:', err);
      setError('Failed to navigate to dashboard. Please try again.');
      setSkipping(false);
    }
  };

  const customActions = (
    <>
      <button
        onClick={handleSkip}
        disabled={generating || skipping}
        className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ backgroundColor: '#D7B472', opacity: 0.5, color: 'white' }}
        onMouseEnter={(e) => { if (!generating && !skipping) e.currentTarget.style.opacity = '0.7'; }}
        onMouseLeave={(e) => { if (!generating && !skipping) e.currentTarget.style.opacity = '0.5'; }}
      >
        {skipping ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Skipping...
          </>
        ) : (
          'Skip for Now'
        )}
      </button>
      
      <button
        onClick={handleGenerate}
        disabled={generating || skipping || !inputData.trim()}
        className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ backgroundColor: '#FF6B5A', color: 'white' }}
        onMouseEnter={(e) => { 
          if (!generating && !skipping && inputData.trim()) e.currentTarget.style.backgroundColor = '#e55a4a'; 
        }}
        onMouseLeave={(e) => { 
          if (!generating && !skipping) e.currentTarget.style.backgroundColor = '#FF6B5A'; 
        }}
      >
        {generating ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Generating...
          </>
        ) : (
          <>
            <Sparkles className="w-5 h-5" />
            Generate & Complete
          </>
        )}
      </button>
    </>
  );

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom right, rgba(255, 107, 90, 0.1), #F8F7F4, rgba(215, 180, 114, 0.1))' }}>
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: 'rgba(255, 107, 90, 0.15)' }}>
            <Brain className="w-8 h-8" style={{ color: '#FF6B5A' }} />
          </div>
          <h1 className="text-4xl font-bold mb-3" style={{ color: '#0A0A10' }}>
            Final Step!
          </h1>
          <p className="text-xl mb-2" style={{ color: '#0A0A10', opacity: 0.7 }}>
            Step 3: Create Your Intelligence Profile
          </p>
          <p className="text-sm mb-6" style={{ color: '#0A0A10', opacity: 0.6 }}>
            Optional: Help AI understand your communication style for better personalization
          </p>
          
          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-2 mt-6">
            <div className="w-8 h-8 rounded-full text-white flex items-center justify-center text-sm" style={{ backgroundColor: '#FF6B5A' }}>
              <CheckCircle className="w-5 h-5" />
            </div>
            <div className="w-16 h-1" style={{ backgroundColor: '#FF6B5A' }}></div>
            <div className="w-8 h-8 rounded-full text-white flex items-center justify-center text-sm" style={{ backgroundColor: '#FF6B5A' }}>
              <CheckCircle className="w-5 h-5" />
            </div>
            <div className="w-16 h-1" style={{ backgroundColor: '#FF6B5A' }}></div>
            <div className="w-8 h-8 rounded-full text-white flex items-center justify-center text-sm font-semibold" style={{ backgroundColor: '#FF6B5A' }}>
              3
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-6 p-4 rounded-lg border" style={{ backgroundColor: 'rgba(215, 180, 114, 0.15)', borderColor: '#D7B472' }}>
            <p style={{ color: '#0A0A10' }}>✓ {successMessage}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 rounded-lg border" style={{ backgroundColor: 'rgba(255, 107, 90, 0.15)', borderColor: '#FF6B5A' }}>
            <p style={{ color: '#FF6B5A' }}>✗ {error}</p>
          </div>
        )}

        {/* Input Section */}
        <UserIntelligenceForm
          inputData={inputData}
          onInputChange={setInputData}
          generating={generating}
          exists={false}
          showActions={true}
          customActions={customActions}
        />

        {/* Info Section */}
        <div className="mt-8 mb-8 rounded-xl shadow-sm border p-6" style={{ backgroundColor: 'rgba(215, 180, 114, 0.1)', borderColor: '#D7B472' }}>
          <h3 className="font-semibold mb-3" style={{ color: '#0A0A10' }}>
            💡 Why create an intelligence profile?
          </h3>
          <ul className="space-y-2 text-sm" style={{ color: '#0A0A10', opacity: 0.8 }}>
            <li>• AI learns your unique communication style and tone</li>
            <li>• Generated content matches your voice and preferences</li>
            <li>• Better personalization leads to more effective collateral</li>
            <li>• You can always update or regenerate your profile later</li>
            <li>• This step is optional - you can skip it and add it later</li>
          </ul>
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            onClick={() => navigate("/tailspin/onboarding/step2")}
            disabled={generating || skipping}
            className="px-8 py-3 rounded-lg font-medium text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#D7B472', opacity: 0.3, color: '#0A0A10' }}
            onMouseEnter={(e) => { if (!generating && !skipping) e.currentTarget.style.opacity = '0.5'; }}
            onMouseLeave={(e) => { if (!generating && !skipping) e.currentTarget.style.opacity = '0.3'; }}
          >
            ← Back to Assets
          </button>
        </div>
      </div>
    </div>
  );
}
